/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Nov 30 2023
 **/

import { createPortal } from "react-dom";
import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";

import { useEventHandler } from "@rfice/eventhandler";
import { useStompClient } from "@/frameworks/libraries/network";
import { ChronoUtils } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Dragger } from "@/frameworks/libraries/components";
import { Icon, ModalPopup, Row, Typography, useModal } from "@/frameworks/designStuff/components";

import * as S from "./private/emotion";
import { ZIndex } from "frameworks/core/commons";
import { EventID, ModalID } from "frameworks/core/commons";
import { useUnityHandler } from "frameworks/core/unity";
import { useKnockManager, useNetworkRPC } from "frameworks/core/hooks";
import { MiniProfile } from "frameworks/core/components";
import { usePersistUser } from "frameworks/core/persistence";
import ReceiveKnock from "../Knock/ReceiveKnock";
import MyDeskKnockApply from "../Knock/MyDeskKnockApply";
import { UserInfo } from "../../hooks/useKnockManager/useKnockManager";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOGTAG = "#MiniProfileContainer#";

const OFF_SET = 16;
export default function MiniProfileContainer() {
	const [userID, setUserID] = useState<string | null>(null);
	const [isKnock, setIsKnock] = useState(false);
	const { knockListValue, knockActions } = useKnockManager();
	const knockList = knockListValue();
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const me = usePersistUser("me");
	const modal = useModal();
	const theme = useTheme();
	const [isLeft, setIsLeft] = useState(false);
	const networkRPC = useNetworkRPC();

	const handleCloseModal = () => {
		modal.hide(ModalID.MYDESK_KNOCK_APPLY);
	};

	const saveFocusKnockCoordinate = (target: Element) => {
		const { top, left } = target.getBoundingClientRect();
		setPosition({ x: left, y: top });
		setIsLeft(true);
	};

	const generateCoordinates = (x: number, y: number) => {
		const newX = x - OFF_SET;
		const newY = y + OFF_SET;

		return !isLeft ? { x, y } : { x: newX, y: newY };
	};

	const handleKnockAction = (userInfo: UserInfo) => {
		setIsKnock(true);
		const knockData = {
			UserInfo: userInfo,
			startCoordinate: generateCoordinates(position.x, position.y),
			zIndex: ZIndex.KNOCK - knockActions.getAll().length,
			uniqueIndex: `${userInfo.id}-${Date.now()}`
		};
		setIsLeft(!isLeft);

		knockActions.show(knockData);
	};

	useEventHandler(EventID.OPEN_MINI_PROFILE, async ({ userId, ...rest }: IPC.Actions.OpenMiniProfile) => {
		console.debug(LOGTAG, "in OPEN_MINI_PROFILE", userId, rest);
		setUserID(userId);
	});

	useEventHandler(
		EventID.CLOSE_MINI_PROFILE,
		async ({ userId }: { userId?: string }) => {
			if (!userId) {
				return;
			}
			if (userId === userID) {
				setUserID(null);
			}
		},
		[userID]
	);

	useUnityHandler("MiniProfileContainer", {
		onExitSpace() {
			setUserID(null);
		}
	});

	/**
	 * 유니티가 사용자에 의해 강제로 종료되었을 때, 스페이스를 나가도록 처리
	 */
	useEventHandler(
		"UnityDomUnmount",
		() => {
			setUserID(null);
		},
		[]
	);

	useEffect(() => {
		if (knockActions.getAll().length === 0) setPosition({ x: 557, y: 360 });
	}, [knockActions.getAll().length]);

	useEffect(() => {
		const centerX = window.innerWidth / 2;
		const centerY = window.innerHeight / 2;

		const elementWidth = 344;
		const elementHeight = 332;

		setPosition({
			x: centerX - elementWidth / 2,
			y: centerY - elementHeight / 2
		});
	}, []);

	useStompClient(
		{
			onMessageReceived(message) {
				const { type, payload } = message;
				if (payload) {
					if (networkRPC.process_rpc_message(type, payload)) {
						return;
					}

					switch (type) {
						case "myDeskKnock": {
							const { userInfo } = payload;
							handleKnockAction(userInfo);
							break;
						}

						case "knockReply": {
							//TODO 현재 해당 부분 기획서에 반영되지 않아 우선적으로 마이데스크 답장하기 기능과 동일하게 유지
							const { userInfo } = payload;
							modal.show({
								id: ModalID.MYDESK_KNOCK_APPLY,
								content: (
									<ModalPopup
										modalID={"MYDESK_KNOCK_APPLY"}
										title={
											<Row>
												<S.IconContainer horizonAlign="center" verticalAlign="center">
													<Icon name="User24" size={18} fill={theme.colors.deco6_50_100} />
												</S.IconContainer>
												<Typography typoColor="neutral_80_100" typoSize="Head6">
													{_T("상대방 깨우기")}
												</Typography>
											</Row>
										}
										confirmText={_T("확인")}
										onConfirm={handleCloseModal}
									>
										<MyDeskKnockApply
											userID={userInfo.id}
											userName={userInfo.name}
											message={payload.content}
											time={ChronoUtils.formatTimeAgo(payload.createdTime)}
										/>
									</ModalPopup>
								),
								zIndex: ZIndex.POPUP
							});
							break;
						}

						case "myDeskKnockReply": {
							const { userInfo } = payload;
							modal.show({
								id: ModalID.MYDESK_KNOCK_APPLY,
								content: (
									<ModalPopup
										modalID={"MYDESK_KNOCK_APPLY"}
										title={
											<Row>
												<S.IconContainer horizonAlign="center" verticalAlign="center">
													<Icon name="User24" size={18} fill={theme.colors.deco6_50_100} />
												</S.IconContainer>
												<Typography typoColor="neutral_80_100" typoSize="Head6">
													{_T("상대방 깨우기")}
												</Typography>
											</Row>
										}
										confirmText={_T("확인")}
										onConfirm={handleCloseModal}
									>
										<MyDeskKnockApply
											userID={userInfo.id}
											userName={userInfo.name}
											message={payload.content}
											time={ChronoUtils.formatTimeAgo(payload.createdTime)}
										/>
									</ModalPopup>
								),
								zIndex: ZIndex.POPUP
							});
							break;
						}
					}
				}
			}
		},
		[me, isLeft, position]
	);

	return createPortal(
		isKnock ? (
			<S.KnockWrapper>
				{knockList?.map((item) => (
					<Dragger
						className={`draggableKnock ${item.UserInfo.id.slice(0, 10)}`}
						key={item.uniqueIndex}
						onPointerDown={() => knockActions.focus(item.uniqueIndex)}
						onPointerUp={(ev) => {
							const target = ev.target as HTMLElement;
							const preventButtonClick = target.closest("button");

							if (!preventButtonClick && item.focus) {
								saveFocusKnockCoordinate(ev.currentTarget as HTMLDivElement);
							}
						}}
						{...item}
					>
						<ReceiveKnock
							userID={item.UserInfo?.id}
							userName={item.UserInfo?.name}
							spaceName={item.SpaceInfo?.name}
							focus={Boolean(item?.focus)}
							uniqueIndex={item.uniqueIndex}
						/>
					</Dragger>
				))}
			</S.KnockWrapper>
		) : (
			<S.MiniProfileWrapper>{userID && <MiniProfile userID={userID} />}</S.MiniProfileWrapper>
		),
		document.body
	);
}
